import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalDateKey } from './dateUtils';
import {
    getCalendarTaskSegment, getMonthCells, getTasksForDate, getTasksForMonth, orderCalendarTasks,
} from './calendarViewUtils';
import { getFlexiblePrecision, getFlexibleRange, getTaskColorClass } from './scheduleUtils';
import { TimelineMonthTasksModal } from './TimelineMonthTasksModal';
import { TimelineYearTaskBar } from './TimelineYearTaskBar';
import { PlannerTaskInfo } from './types';
import { useTimelineView } from './TimelineViewContext';

const getWeekdays = (language: string) => Array.from({ length: 7 }, (_, index) => (
    new Date(2024, 0, index + 1).toLocaleDateString(language, { weekday: 'narrow' })
));

const getMonthTasks = (tasks: PlannerTaskInfo[], month: Date) => tasks.filter(({ task }) => {
    if (getFlexiblePrecision(task) !== 'month') return false;
    const { start, end } = getFlexibleRange(task);
    const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59, 999);
    return start <= monthEnd && end >= month;
});

const isMultiDayTask = (task: PlannerTaskInfo['task']) => {
    if (getFlexiblePrecision(task) !== 'day') return false;
    const { start, end } = getFlexibleRange(task);
    return start.toDateString() !== end.toDateString();
};

const TimelineYearMonth: React.FC<{ date: Date; onOpen: () => void; tasks: PlannerTaskInfo[]; weekdays: string[] }> = ({
    date, onOpen, tasks, weekdays,
}) => {
    const { i18n } = useTranslation();
    const { onScaleChange } = useTimelineView();
    const todayKey = getLocalDateKey(new Date());
    const monthTasks = getTasksForMonth(tasks, date);
    const monthMarkers = orderCalendarTasks(getMonthTasks(tasks, date));
    const label = date.toLocaleDateString(i18n.language, { month: 'long' });
    return (
        <div className="timeline-year-month">
            <span className="timeline-year-month-heading">
                <button type="button" className="timeline-year-month-title"
                    onClick={() => onScaleChange('month', date)}>{label}</button>
                <span className={`timeline-year-month-count ${monthTasks.length ? '' : 'is-empty'}`}>
                    {monthTasks.length}
                </span>
            </span>
            <button type="button" className="timeline-year-month-calendar" onClick={onOpen} aria-label={label}>
                <span className="timeline-year-month-markers">
                    {monthMarkers.map(({ task }) => <span key={task.id} data-timeline-task-id={task.id}
                        className={`${getTaskColorClass(task)} ${task.completed ? 'completed' : ''}`}
                        title={task.title} />)}
                </span>
                <span className="timeline-year-weekdays">{weekdays.map((weekday, index) => (
                    <span key={`${weekday}-${index}`}>{weekday}</span>
                ))}</span>
                <span className="timeline-year-days">{getMonthCells(date).map((day, index) => {
                    if (!day) return <span key={`empty-${index}`} className="timeline-year-day is-empty" />;
                    const dayTasks = orderCalendarTasks(getTasksForDate(tasks, day));
                    const rangeTasks = dayTasks.filter(({ task }) => isMultiDayTask(task)).slice(0, 2);
                    const dotTasks = dayTasks.filter(({ task }) => !isMultiDayTask(task)).slice(0, 3);
                    return <span key={getLocalDateKey(day)}
                        className={`timeline-year-day ${todayKey === getLocalDateKey(day) ? 'is-today' : ''}`}
                        data-range-count={rangeTasks.length || undefined}>
                        <span>{day.getDate()}</span>
                        {rangeTasks.length > 0 && <span className="timeline-year-day-ranges">
                            {rangeTasks.map(({ task }) => <span key={task.id} data-timeline-task-id={task.id}
                                className={`timeline-year-range ${getCalendarTaskSegment(task, day, date).className} ${task.completed ? 'completed' : ''}`} />)}
                        </span>}
                        {dotTasks.length > 0 && <span className="timeline-year-day-markers">
                            {dotTasks.map(({ task }) => <span key={task.id} data-timeline-task-id={task.id}
                                className={`${getTaskColorClass(task)} ${task.completed ? 'completed' : ''}`}
                                title={task.title} />)}
                        </span>}
                    </span>;
                })}</span>
            </button>
        </div>
    );
};

export const TimelineYearCalendar: React.FC<{ current: boolean; date: Date }> = ({ current, date }) => {
    const { i18n } = useTranslation();
    const { onRequestTaskListReturn, onScaleChange, onTaskListOpened, taskListToOpen, tasksWithDueDates } = useTimelineView();
    const [openMonth, setOpenMonth] = useState<Date | null>(null);
    const weekdays = useMemo(() => getWeekdays(i18n.language), [i18n.language]);
    const year = date.getFullYear();
    React.useEffect(() => {
        if (taskListToOpen?.scope !== 'month' || taskListToOpen.date.getFullYear() !== year) return;
        setOpenMonth(taskListToOpen.date);
        onTaskListOpened();
    }, [onTaskListOpened, taskListToOpen, year]);
    return <section className="timeline-period timeline-period-year timeline-year-calendar"
        data-timeline-current-period={current || undefined} data-timeline-label={year}
        data-timeline-period-key={`year-${getLocalDateKey(date)}`} data-timeline-date={getLocalDateKey(date)}
        data-timeline-year-date={getLocalDateKey(date)}>
        <header className="timeline-year-calendar-heading"><h2><button type="button"
            className="timeline-year-calendar-title" onClick={() => onScaleChange('years', date)}>{year}</button></h2></header>
        <TimelineYearTaskBar year={year} />
        <div className="timeline-year-calendar-grid">{Array.from({ length: 12 }, (_, month) => {
            const monthDate = new Date(year, month, 1);
            return <TimelineYearMonth key={monthDate.toISOString()} date={monthDate} tasks={tasksWithDueDates}
                weekdays={weekdays} onOpen={() => setOpenMonth(monthDate)} />;
        })}</div>
        {openMonth && <TimelineMonthTasksModal date={openMonth} onClose={() => setOpenMonth(null)}
            onPrepareAction={() => {
                onRequestTaskListReturn('month', openMonth);
                setOpenMonth(null);
            }} />}
    </section>;
};
